/** TagTypeName used by the global Netwalk4 tag catalog for Art News. */
export const NEWS_TAG_TYPE = 'News';

export const NEWS_STATUS = {
    DRAFT: 'draft',
    PUBLISHED: 'published',
};

export const NEWS_STATUS_FILTER_ALL = 'all';

export const NEWS_STATUS_OPTIONS = [
    { value: NEWS_STATUS.DRAFT, label: 'Draft' },
    { value: NEWS_STATUS.PUBLISHED, label: 'Published' },
];

export const NEWS_STATUS_FILTER_OPTIONS = [
    { value: NEWS_STATUS_FILTER_ALL, label: 'All' },
    ...NEWS_STATUS_OPTIONS,
];

export const NEWS_STATUS_SEGMENTED_OPTIONS = [
    { value: NEWS_STATUS_FILTER_ALL, label: 'All' },
    { value: NEWS_STATUS.DRAFT, label: 'Draft' },
    { value: NEWS_STATUS.PUBLISHED, label: 'Published' },
];

export const DEFAULT_NEWS_FILTERS = {
    modelId: null,
    sectionId: null,
    status: NEWS_STATUS_FILTER_ALL,
    text: null,
    userCreated: null,
};

export const NEWS_LINK_TARGET = {
    ARTICLE: 'article',
    CONTENT: 'content',
    EXTERNAL: 'external',
    GALLERY: 'gallery',
    MODEL: 'model',
};

export const NEWS_LINK_TARGET_FILTER_ALL = 'all';

export const NEWS_LINK_TARGET_OPTIONS = [
    { value: NEWS_LINK_TARGET.CONTENT, label: 'No link' },
    { value: NEWS_LINK_TARGET.ARTICLE, label: 'Article' },
    { value: NEWS_LINK_TARGET.MODEL, label: 'Model page' },
    { value: NEWS_LINK_TARGET.GALLERY, label: 'Gallery' },
    { value: NEWS_LINK_TARGET.EXTERNAL, label: 'External URL' },
];

export const NEWS_LINK_TARGET_FILTER_OPTIONS = [
    { value: NEWS_LINK_TARGET_FILTER_ALL, label: 'All' },
    ...NEWS_LINK_TARGET_OPTIONS,
];

export const NEWS_COVER_SOURCE = {
    EMBED: 'embed',
    NETWALK: 'netwalk',
    UPLOAD: 'upload',
    UPLOADED: 'uploaded',
};

export const NEWS_COVER_MEDIA_TYPE = {
    IMAGE: 'image',
    VIDEO: 'video',
};

export const NEWS_COVER_EMBED_PROVIDER = {
    VIMEO: 'vimeo',
    YOUTUBE: 'youtube',
};

export const normalizeNewsStatus = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    return normalizedStatus === NEWS_STATUS.PUBLISHED ? NEWS_STATUS.PUBLISHED : NEWS_STATUS.DRAFT;
};

export const normalizeNewsStatusFilter = (status) => {
    const validStatuses = [NEWS_STATUS_FILTER_ALL, NEWS_STATUS.DRAFT, NEWS_STATUS.PUBLISHED];
    return validStatuses.includes(status) ? status : NEWS_STATUS_FILTER_ALL;
};

/** Maps internal status to the CMS API value. */
export const toApiNewsStatus = (status) => {
    return normalizeNewsStatus(status) === NEWS_STATUS.PUBLISHED ? 'Published' : 'Draft';
};

/** Maps CMS API status filter to the search payload value. */
export const toApiNewsStatusFilter = (status) => {
    const normalizedStatus = normalizeNewsStatusFilter(status);
    if (normalizedStatus === NEWS_STATUS_FILTER_ALL) return null;
    return toApiNewsStatus(normalizedStatus);
};

const API_LINK_TARGET_BY_INTERNAL = {
    [NEWS_LINK_TARGET.ARTICLE]: 'Article',
    [NEWS_LINK_TARGET.CONTENT]: null,
    [NEWS_LINK_TARGET.EXTERNAL]: 'External',
    [NEWS_LINK_TARGET.GALLERY]: 'Gallery',
    [NEWS_LINK_TARGET.MODEL]: 'Model',
};

const INTERNAL_LINK_TARGET_BY_API = {
    article: NEWS_LINK_TARGET.ARTICLE,
    external: NEWS_LINK_TARGET.EXTERNAL,
    gallery: NEWS_LINK_TARGET.GALLERY,
    model: NEWS_LINK_TARGET.MODEL,
};

export const normalizeNewsLinkTarget = (linkTarget) => {
    const normalizedLinkTarget = String(linkTarget || '').toLowerCase();
    const validTargets = Object.values(NEWS_LINK_TARGET);
    if (validTargets.includes(normalizedLinkTarget)) {
        return normalizedLinkTarget;
    }
    return INTERNAL_LINK_TARGET_BY_API[normalizedLinkTarget] || NEWS_LINK_TARGET.CONTENT;
};

/** Maps internal link target to the CMS API LinkTargetType value. */
export const toApiNewsLinkTargetType = (linkTarget) => {
    const normalizedLinkTarget = normalizeNewsLinkTarget(linkTarget);
    return API_LINK_TARGET_BY_INTERNAL[normalizedLinkTarget] ?? null;
};

const normalizeNewsLinkTargetId = (linkTargetId) => {
    if (linkTargetId === undefined || linkTargetId === null || linkTargetId === '') return linkTargetId;

    const numericLinkTargetId = Number(linkTargetId);
    return Number.isInteger(numericLinkTargetId) ? numericLinkTargetId : linkTargetId;
};

/** Builds the editor-friendly LinkTarget object from CMS API fields. */
export const buildNewsLinkTargetFromApi = (news = {}) => {
    const linkTargetType = normalizeNewsLinkTarget(news.LinkTargetType);
    const linkTargetId = normalizeNewsLinkTargetId(news.LinkTargetID ?? news.LinkTargetId);

    if (linkTargetType === NEWS_LINK_TARGET.CONTENT) {
        return { type: NEWS_LINK_TARGET.CONTENT };
    }

    if (linkTargetType === NEWS_LINK_TARGET.EXTERNAL) {
        return {
            externalUrl: news.LinkTargetUrl || undefined,
            type: NEWS_LINK_TARGET.EXTERNAL,
        };
    }

    if (linkTargetType === NEWS_LINK_TARGET.ARTICLE) {
        return {
            articleId: linkTargetId,
            type: NEWS_LINK_TARGET.ARTICLE,
        };
    }

    if (linkTargetType === NEWS_LINK_TARGET.GALLERY) {
        return {
            galleryId: linkTargetId,
            type: NEWS_LINK_TARGET.GALLERY,
        };
    }

    if (linkTargetType === NEWS_LINK_TARGET.MODEL) {
        return {
            modelId: linkTargetId,
            type: NEWS_LINK_TARGET.MODEL,
        };
    }

    return { type: NEWS_LINK_TARGET.CONTENT };
};

export const normalizeNewsLinkTargetFilter = (linkTarget) => {
    const validTargets = [NEWS_LINK_TARGET_FILTER_ALL, ...Object.values(NEWS_LINK_TARGET)];
    return validTargets.includes(linkTarget) ? linkTarget : NEWS_LINK_TARGET_FILTER_ALL;
};

/** News items whose link target is this article (only type Article counts). */
export const findNewsReferencingArticle = (newsList = [], articleId) => {
    if (!articleId) return [];

    return newsList.filter((news) => {
        const linkTarget = news.LinkTarget || buildNewsLinkTargetFromApi(news);
        const linkType = normalizeNewsLinkTarget(linkTarget.type || news.LinkTargetType);
        if (linkType !== NEWS_LINK_TARGET.ARTICLE) return false;
        const linkedArticleId = linkTarget.articleId ?? news.LinkTargetID ?? news.LinkTargetId;
        return String(linkedArticleId ?? '') === String(articleId);
    });
};

export const normalizeNewsCoverMediaType = (mediaType) => {
    return mediaType === NEWS_COVER_MEDIA_TYPE.VIDEO ? NEWS_COVER_MEDIA_TYPE.VIDEO : NEWS_COVER_MEDIA_TYPE.IMAGE;
};

export const getNewsCoverMediaType = (cover = {}) => {
    if (cover.mediaType) {
        return normalizeNewsCoverMediaType(cover.mediaType);
    }
    if (cover.source === NEWS_COVER_SOURCE.EMBED) {
        return NEWS_COVER_MEDIA_TYPE.VIDEO;
    }
    if (cover.embedUrl || cover.videoUrl) {
        return NEWS_COVER_MEDIA_TYPE.VIDEO;
    }
    return NEWS_COVER_MEDIA_TYPE.IMAGE;
};

const getValidYoutubeId = (value) => {
    const youtubeId = String(value || '').trim();
    return /^[a-zA-Z0-9_-]{11}$/.test(youtubeId) ? youtubeId : null;
};

const getYoutubeIdFromUrl = (url) => {
    const host = url.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be') {
        return getValidYoutubeId(url.pathname.split('/').filter(Boolean)[0]);
    }

    if (host !== 'youtube.com' && host !== 'youtube-nocookie.com' && host !== 'm.youtube.com') {
        return null;
    }

    const watchId = getValidYoutubeId(url.searchParams.get('v'));
    if (watchId) {
        return watchId;
    }

    const pathParts = url.pathname.split('/').filter(Boolean);
    if (['embed', 'shorts', 'live'].includes(pathParts[0])) {
        return getValidYoutubeId(pathParts[1]);
    }

    return null;
};

const getValidVimeoId = (value) => {
    const vimeoId = String(value || '').trim();
    return /^\d{6,12}$/.test(vimeoId) ? vimeoId : null;
};

const getValidVimeoHash = (value) => {
    const vimeoHash = String(value || '').trim();
    return /^[a-zA-Z0-9]+$/.test(vimeoHash) ? vimeoHash : null;
};

const getVimeoUrlFromUrl = (url) => {
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    if (host !== 'vimeo.com' && host !== 'player.vimeo.com') {
        return null;
    }

    const pathParts = url.pathname.split('/').filter(Boolean);
    const isPlayerUrl = host === 'player.vimeo.com';
    if (isPlayerUrl && pathParts[0] !== 'video') {
        return null;
    }

    const vimeoId = getValidVimeoId(isPlayerUrl ? pathParts[1] : pathParts[0]);
    if (!vimeoId) return null;

    const vimeoHash = getValidVimeoHash(isPlayerUrl ? url.searchParams.get('h') : pathParts[1]);
    return vimeoHash ? `https://vimeo.com/${vimeoId}/${vimeoHash}` : `https://vimeo.com/${vimeoId}`;
};

export const getNewsVideoEmbedData = (value) => {
    const sourceValue = String(value || '').trim();
    const iframeSrcMatch = sourceValue.match(/src=["']([^"']+)["']/i);
    const videoUrl = iframeSrcMatch ? iframeSrcMatch[1] : sourceValue;
    if (!videoUrl) return null;

    try {
        const url = new URL(videoUrl.startsWith('//') ? `https:${videoUrl}` : videoUrl);
        const youtubeId = getYoutubeIdFromUrl(url);
        if (youtubeId) {
            return {
                provider: NEWS_COVER_EMBED_PROVIDER.YOUTUBE,
                thumbnailUrl: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
                url: `https://www.youtube.com/watch?v=${youtubeId}`,
            };
        }

        const vimeoUrl = getVimeoUrlFromUrl(url);
        if (vimeoUrl) {
            return {
                provider: NEWS_COVER_EMBED_PROVIDER.VIMEO,
                url: vimeoUrl,
            };
        }
    } catch (error) {
        return null;
    }

    return null;
};

const decodeHtmlEntities = (value) => {
    const entities = {
        '&amp;': '&',
        '&gt;': '>',
        '&lt;': '<',
        '&nbsp;': ' ',
        '&quot;': '"',
        '&#39;': "'",
        '&#x27;': "'",
    };

    let decodedValue = value;
    for (let i = 0; i < 2; i += 1) {
        decodedValue = decodedValue.replace(
            /&(?:amp|gt|lt|nbsp|quot|#39|#x27);/gi,
            (entity) => entities[entity.toLowerCase()] || entity
        );
    }
    return decodedValue;
};

const stripHtmlTags = (value) => {
    return value
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

/** Plain text for search / indexing (Quill HTML and similar). */
export const htmlToPlainSearchText = (html = '') => {
    const decoded = decodeHtmlEntities(String(html));
    return stripHtmlTags(decoded);
};

export const getNewsAbstractText = (abstract = '') => htmlToPlainSearchText(abstract);
